import CustomSelectEx from '@/ctComponents/CustomSelectEx';
import React, { useEffect, useState } from 'react';

const hourPriorityOptions = [
  { label: 'High', value: 'HIGH' },
  { label: 'Medium', value: 'MEDIUM' },
  { label: 'Low', value: 'LOW' },
  { label: 'Exclude', value: 'EXCLUDE' },
  { label: '', value: '' },
];

const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

// Helper to format hour range
const formatHourRange = (start, end) => {
  const pad = (n) => n.toString().padStart(2, '0');
  return `${pad(start)}:00 - ${pad(end)}:00`;
};

// Helper to get start/end hour from range index and timeRange
const getRangeStartEnd = (idx, timeRange) => {
  const start = idx * timeRange;
  let end = start + timeRange;
  if (end > 24) end = 24;
  return [start, end];
};

const HourPreferences = ({
  hourSelections,
  setHourSelections,
  hourPriority,
  setHourPriority,
}) => {
  // State for time range (1, 2, 3)
  const [timeRange, setTimeRange] = useState(1);
  // State for draft selection: [startIdx, endIdx]
  const [rangeDraft, setRangeDraft] = useState([null, null]);
  // State for hover index
  const [hoverIdx, setHoverIdx] = useState(null);

  // Generate ranges based on timeRange
  const ranges = Array.from({ length: Math.ceil(24 / timeRange) }, (_, i) => {
    const [start, end] = getRangeStartEnd(i, timeRange);
    return { idx: i, start, end };
  });

  // Check if a range overlaps with any selected
  const isRangeOverlapping = (start, end) => {
    return hourSelections?.some((range) => {
      const rStart = parseInt(range.time_start.split(':')[0], 10);
      const rEnd = parseInt(range.time_end.split(':')[0], 10);
      // overlap if any part of [start, end) is inside [rStart, rEnd)
      return Math.max(start, rStart) < Math.min(end, rEnd);
    });
  };

  // Check if a range is already selected (by exact match)
  const isRangeAlreadySelected = (start, end) => {
    return hourSelections?.some((range) => {
      const rStart = parseInt(range.time_start.split(':')[0], 10);
      const rEnd = parseInt(range.time_end.split(':')[0], 10);
      return start === rStart && end === rEnd;
    });
  };

  // Handle click on a range box
  const handleRangeClick = (idx) => {
    if (rangeDraft[0] === null) {
      setRangeDraft([idx, null]);
    } else if (rangeDraft[0] !== null && rangeDraft[1] === null) {
      if (idx === rangeDraft[0]) {
        setRangeDraft([null, null]);
        setHoverIdx(null);
        return;
      }
      const startIdx = Math.min(rangeDraft[0], idx);
      const endIdx = Math.max(rangeDraft[0], idx);
      // بازه واقعی انتخابی
      const draftStart = ranges[startIdx].start;
      const draftEnd = ranges[endIdx].end;
      // بررسی تداخل با بازه‌های انتخاب‌شده
      let overlap = false;
      for (let i = startIdx; i <= endIdx; i++) {
        const rStart = ranges[i].start;
        const rEnd = ranges[i].end;
        if (isRangeOverlapping(rStart, rEnd)) {
          overlap = true;
          break;
        }
      }
      if (!overlap) {
        setRangeDraft([startIdx, endIdx]);
      } else {
        setRangeDraft([null, null]);
        setHoverIdx(null);
      }
    } else {
      setRangeDraft([idx, null]);
      setHoverIdx(null);
    }
  };

  // Handle hover
  const handleRangeMouseEnter = (idx) => {
    if (rangeDraft[0] !== null && rangeDraft[1] === null) {
      setHoverIdx(idx);
    }
  };
  const handleRangeMouseLeave = () => setHoverIdx(null);

  // Get classes for each range box
  const getRangeClasses = (idx) => {
    const [start, end] = rangeDraft;
    let classes = ['hour-box'];
    const [rangeStart, rangeEnd] = getRangeStartEnd(idx, timeRange);
    // Priority color if already selected
    const selected = hourSelections?.find((range) => {
      const rStart = parseInt(range.time_start.split(':')[0], 10);
      const rEnd = parseInt(range.time_end.split(':')[0], 10);
      return rangeStart >= rStart && rangeEnd <= rEnd;
    });
    if (selected) {
      classes.push(`priority-${selected.priority.toLowerCase()}`);
      return classes.join(' ');
    }
    // Final selection (draft)
    if (start !== null && end !== null && idx >= start && idx <= end) {
      classes.push('hour-selected');
      if (idx === start) classes.push('hour-start');
      else if (idx === end) classes.push('hour-end');
      else classes.push('hour-between');
      return classes.join(' ');
    }
    // Only start selected (single click)
    if (start !== null && end === null && idx === start) {
      classes.push('hour-selected', 'hour-start');
      return classes.join(' ');
    }
    // Hover selection
    if (start !== null && end === null && hoverIdx !== null) {
      const hoverStart = Math.min(start, hoverIdx);
      const hoverEnd = Math.max(start, hoverIdx);
      if (idx >= hoverStart && idx <= hoverEnd) {
        classes.push('hour-hover');
        if (idx === hoverStart) classes.push('hour-hover-start');
        else if (idx === hoverEnd) classes.push('hour-hover-end');
        else classes.push('hour-hover-between');
        return classes.join(' ');
      }
    }
    return classes.join(' ');
  };

  // Add selected range to list (merge as one range)
  const handleAddToList = () => {
    const [start, end] = rangeDraft;
    if (start !== null && hourPriority && hourPriority !== 'None') {
      let draftStart, draftEnd;

      if (end === null) {
        // Single range selection
        [draftStart, draftEnd] = getRangeStartEnd(start, timeRange);
      } else {
        // Multiple range selection
        draftStart = ranges[start].start;
        draftEnd = ranges[end].end;
      }

      // Check for overlaps
      let overlap = false;
      const checkStart = start;
      const checkEnd = end === null ? start : end;

      for (let i = checkStart; i <= checkEnd; i++) {
        const rStart = ranges[i].start;
        const rEnd = ranges[i].end;
        if (isRangeOverlapping(rStart, rEnd)) {
          overlap = true;
          break;
        }
      }

      if (!overlap) {
        setHourPriority('');
        setHourSelections([
          ...hourSelections,
          {
            time_start: `${draftStart.toString().padStart(2, '0')}:00`,
            time_end: `${draftEnd.toString().padStart(2, '0')}:00`,
            priority: hourPriority.toUpperCase(),
          },
        ]);
        setRangeDraft([null, null]);
        setHoverIdx(null);
      }
    }
  };
  // Disable add button
  const isAddButtonDisabled = () => {
    const [start] = rangeDraft;
    return start === null || hourPriority === 'None';
  };

  // Remove selection
  const handleRemove = (i) => {
    setHourSelections((prev) => prev.filter((_, idx) => idx !== i));
  };

  return (
    <div className="hour-section">
      <div className="time-picker-section">
        {/* <div
          style={{
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 16,
          }}>
          <span style={{ fontWeight: 500 }}>Select Time Renge:</span>
          {[1, 2, 3].map((tr) => (
            <label key={tr} style={{ marginRight: 16, cursor: 'pointer' }}>
              <input
                type="radio"
                checked={timeRange === tr}
                onChange={() => {
                  setTimeRange(tr);
                  setRangeDraft([null, null]);
                  setHoverIdx(null);
                }}
                style={{ marginRight: 4 }}
              />
              {tr} Hour
            </label>
          ))}
        </div> */}
        <div className="hour-grid">
          {ranges.map((r) => (
            <div
              key={r.idx}
              className={`min-w-70 lg:min-w-80 ${getRangeClasses(r.idx)}`}
              onClick={() => handleRangeClick(r.idx)}
              onMouseEnter={() => handleRangeMouseEnter(r.idx)}
              onMouseLeave={handleRangeMouseLeave}>
              {formatHourRange(r.start, r.end)}
            </div>
          ))}
        </div>
        <div className="hour-actions">
          <CustomSelectEx
            width={'69%'}
            placeholder="Select Priority"
            value={
              hourPriorityOptions.find((opt) => opt.value === hourPriority) ||
              ''
            }
            onChange={(item) => setHourPriority(item?.value || '')}
            options={hourPriorityOptions}
            dropdown={true}
          />
          <button
            className="btn"
            onClick={handleAddToList}
            disabled={isAddButtonDisabled()}>
            Add to List
          </button>
        </div>
      </div>
      <div className="selected-times-section">
        <span className="section-title">Your Selected Times:</span>
        {hourSelections?.length > 0 ? null : (
          <p className="selected-times-section-description">
            As soon as you set up your Pefrences, all your saved items will be
            displayed here for easy access.
          </p>
        )}
        <div className="hour-list">
          {hourSelections?.map((range, i) => (
            <div key={i} className="hour-tag">
              <span>
                {range.time_start} to {range.time_end} -{' '}
                {capitalize(range.priority.toLowerCase())}
              </span>
              <button onClick={() => handleRemove(i)} className="remove">
                ×
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HourPreferences;
